import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Toast, ToastVariant, useToast } from '@hungryroot/components-toast';
import { Check } from '@hungryroot/icons';

import styles from './TriggerToasts.module.css';

export function TriggerToasts() {
  const { toast } = useToast();

  const onTriggerToast = useCallback(
    (toastType: ValidToastParam) => {
      let variant;
      let message;

      switch (toastType) {
        case ToastParams.CartSaved:
          variant = ToastVariant.Neutral;
          message = (
            <>
              <Check className={styles.icon} />
              Your cart has been saved!
            </>
          );
          break;

        case ToastParams.WinbackOfferClaimed:
          variant = ToastVariant.Success;
          message = "You're all set! Your discount will apply to your order.";
          break;

        case ToastParams.Reactivated:
        case ToastParams.UnpausedOrder:
          variant = ToastVariant.Neutral;
          message = (
            <>
              <Check className={styles.icon} />
              Welcome back! It'll just take a few seconds to fill your cart.
            </>
          );
          break;
      }

      if (variant !== undefined && message) {
        toast(
          <Toast variant={variant} className={styles.toast}>
            {message}
          </Toast>
        );
      }
    },
    [toast]
  );

  useTriggerToasts({ onTriggerToast });

  return null;
}

enum ToastParams {
  CartSaved = 'cart-saved',
  WinbackOfferClaimed = 'winback-offer-claimed',
  UnpausedOrder = 'unpaused-order',
  Reactivated = 'reactivated',
}

type ValidToastParam = `${ToastParams}`;

const validToastParams = Object.values(ToastParams) as ValidToastParam[];

type TriggerToastProps = {
  onTriggerToast: (toastType: ValidToastParam) => void;
};

export function useTriggerToasts(props: TriggerToastProps) {
  const { onTriggerToast } = props;

  const location = useLocation();
  const history = useHistory();
  const processedToastsRef = useRef<string>('');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const toastValue = searchParams.get('toast') as ValidToastParam | null;

    if (
      toastValue &&
      validToastParams.includes(toastValue) &&
      processedToastsRef.current !== location.search
    ) {
      searchParams.delete('toast');

      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });

      onTriggerToast(toastValue);
    }

    processedToastsRef.current = location.search;
  }, [history, location.pathname, location.search, onTriggerToast]);
}
